import { useState, useCallback, useMemo, useEffect, useRef } from "react";
import { useLocation } from "wouter";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Plus,
  FileText,
  Loader2,
  Pencil,
  Ban,
  Lock,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  ChevronLeft,
  ChevronRight,
  Filter,
  X,
  CalendarRange,
  DollarSign,
  HelpCircle,
  Copy,
  Check,
  Download,
} from "lucide-react";
import { format, isFuture } from "date-fns";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/context/AuthContext";
import { isAgencyOwnerRole } from "@/lib/roles";
import { useImpersonation } from "@/context/ImpersonationContext";
import { useVoidInvoice, useInvoiceList, buildInvoiceListQuery, InvoiceLockedByClaimError } from "@/hooks/useInvoices";
import type { InvoiceListFilters, InvoiceListRow, InvoiceListTotals } from "@/hooks/useInvoices";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import UnsavedChangesDialog from "@/components/UnsavedChangesDialog";
import { CommissionInquiryDialog } from "./CommissionInquiryDialog";
import { VoidInvoiceConfirmDescription } from "./VoidInvoiceConfirmDescription";
import { SectionLoadError } from "./SectionErrorBoundary";
import { FINANCES_PATHS } from "@/lib/financesPaths";
import { financesTable } from "@/lib/financesTypography";
import { nextSortState } from "@/lib/tableSort";
import { formatTramsInvoiceDisplayNumber } from "@shared/invoice-display";

const PAGE_SIZE = 10;

/* AR-316 — v9 renders invoice state as a tinted pill rather than coloured text
   (migration-docs/v9 Finances: `paid`, `due Sep 1`, `blocked — card expired`).
   `color` is kept for callers that still want the bare text treatment. */
const invoiceStatusConfig: Record<
  string,
  { label: string; color: string; chip: string }
> = {
  O: {
    label: "Open",
    color: "text-[#3b82f6]",
    chip: "bg-blue-50 text-blue-700 dark:bg-blue-950/50 dark:text-blue-300",
  },
  C: {
    label: "Closed",
    color: "text-[#10b981]",
    chip: "bg-emerald-50 text-emerald-700 dark:bg-emerald-950/50 dark:text-emerald-300",
  },
  V: {
    label: "Voided",
    color: "text-gray-400",
    chip: "bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400",
  },
};

const STATUS_CHIP =
  "inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold leading-none";

type SortField =
  | "advisorName"
  | "tripId"
  | "recordLocator"
  | "invoiceNumber"
  | "primaryTraveler"
  | "supplierName"
  | "confirmationNumber"
  | "departureDate"
  | "returnDate"
  | "totalFare"
  | "commissionAmount"
  | "invoiceStatusCode"
  | "createdAt"
  | "paymentDate";

function SortableHeader({
  label,
  field,
  currentSort,
  currentDir,
  onSort,
  className,
}: {
  label: string;
  field: SortField;
  currentSort: string;
  currentDir: "asc" | "desc";
  onSort: (field: SortField) => void;
  className?: string;
}) {
  const isActive = currentSort === field;
  return (
    <TableHead
      className={className}
      scope="col"
      aria-sort={isActive ? (currentDir === "asc" ? "ascending" : "descending") : "none"}
    >
      <button
        type="button"
        className={`${financesTable.headSortable} gap-1 hover:text-foreground transition-colors text-left whitespace-nowrap`}
        onClick={() => onSort(field)}
      >
        {label}
        {/* AR-210 (TC-26.1): aria-sort sits on the th, but focus is on this button, so
            screen readers never heard the state. Say it in the button's own name. */}
        <span className="sr-only">
          {isActive ? `, sorted ${currentDir === "asc" ? "ascending" : "descending"}` : ", not sorted"}
        </span>
        {isActive ? (
          currentDir === "asc" ? <ArrowUp className="h-3 w-3" aria-hidden="true" /> : <ArrowDown className="h-3 w-3" aria-hidden="true" />
        ) : (
          <ArrowUpDown className="h-3 w-3 opacity-40" aria-hidden="true" />
        )}
      </button>
    </TableHead>
  );
}


function parseDateAsLocal(value: string): Date {
  return new Date(value.substring(0, 10) + "T00:00:00");
}

function formatPaymentDate(row: InvoiceListRow): { text: string; isInterim: boolean } {
  if (!row.paymentDate) return { text: "—", isInterim: false };
  const d = new Date(row.paymentDate);
  return { text: format(d, "MM/dd/yyyy"), isInterim: isFuture(d) };
}

/* AR-411: totals are signed — a net-negative filtered set (refunds,
   adjustments) must read "-$1,234.56", not "$-1,234.56". */
const totalsCurrency = new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" });
function formatTotal(value: string | undefined): string {
  const n = parseFloat(value ?? "");
  return Number.isFinite(n) ? totalsCurrency.format(n) : "—";
}

/* AR-411: compact totals strip. Below `md` the money columns are folded into
   the Primary Traveler cell, so the table footer has nothing to sit under;
   it also stands in for the footer when no rows match (the empty state
   replaces the table). Always shows $0.00 rather than blank. */
function InvoiceLogTotalsBar({
  totals,
  totalCount,
  showNetCommission,
  className = "",
}: {
  totals: InvoiceListTotals | undefined;
  totalCount: number;
  showNetCommission: boolean;
  className?: string;
}) {
  const cells: Array<[string, string]> = [
    ["Total Sales", formatTotal(totals?.totalFare ?? "0")],
    ["Gross Comm.", formatTotal(totals?.commissionAmount ?? "0")],
    ...(showNetCommission ? [["Net Comm.", formatTotal(totals?.agentCommissionAmount ?? "0")] as [string, string]] : []),
  ];
  return (
    <div
      data-testid="invoice-log-totals-bar"
      className={`mt-2 rounded-md border bg-muted/40 px-3 py-2 ${className}`}
      role="group"
      aria-label="Invoice log totals"
    >
      <p className={`${financesTable.headStatic} mb-1`}>
        Totals · {totalCount.toLocaleString()} invoice{totalCount !== 1 ? "s" : ""}
      </p>
      <dl className="grid grid-cols-2 gap-x-4 gap-y-1 sm:flex sm:flex-wrap sm:gap-x-6">
        {cells.map(([label, value]) => (
          <div key={label} className="flex items-baseline justify-between gap-2 sm:justify-start">
            <dt className="text-xs text-muted-foreground">{label}</dt>
            <dd className="text-sm font-semibold tabular-nums">{value}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
}

function generatePageNumbers(current: number, total: number): (number | "...")[] {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const pages: (number | "...")[] = [1];
  const start = Math.max(2, current - 1);
  const end = Math.min(total - 1, current + 1);
  if (start > 2) pages.push("...");
  for (let i = start; i <= end; i++) pages.push(i);
  if (end < total - 1) pages.push("...");
  pages.push(total);
  return pages;
}

interface InvoiceLogTableProps {
  tramsAccountId?: number;
  viewMode: "my" | "all";
  onViewModeChange: (mode: "my" | "all") => void;
  hasCompanyAccess: boolean;
}

export function InvoiceLogTable({
  tramsAccountId,
  viewMode,
  onViewModeChange,
  hasCompanyAccess,
}: InvoiceLogTableProps) {
  const VOID_POLL_INTERVAL_MS = 5_000;
  const VOID_POLL_TIMEOUT_MS = 120_000;
  const [, setLocation] = useLocation();
  const { user } = useAuth();
  const { impersonation } = useImpersonation();
  const effectiveUserId = impersonation.active && impersonation.targetUserId
    ? impersonation.targetUserId
    : user?.id;
  // Net Comm. is Agency Owner-only (same gating as the snapshot net KPIs).
  const effectiveRole = impersonation.active ? impersonation.targetRole : user?.role;
  const showNetCommission = isAgencyOwnerRole(effectiveRole);
  const { toast } = useToast();
  const voidInvoice = useVoidInvoice();
  const [voidTarget, setVoidTarget] = useState<InvoiceListRow | null>(null);
  const [voidReason, setVoidReason] = useState("");
  const [showDiscardDialog, setShowDiscardDialog] = useState(false);
  const [pendingVoidIds, setPendingVoidIds] = useState<Array<{ id: number; startedAt: number }>>([]);
  const [inquiryInvoiceId, setInquiryInvoiceId] = useState<number | null>(null);
  const [copiedInvoiceId, setCopiedInvoiceId] = useState<number | null>(null);
  const filterTravelerRef = useRef<HTMLInputElement>(null);

  const copyInvoiceNumber = useCallback(async (row: InvoiceListRow) => {
    try {
      await navigator.clipboard.writeText(formatTramsInvoiceDisplayNumber(row.tramsInvoiceNumber));
      setCopiedInvoiceId(row.id);
      setTimeout(() => setCopiedInvoiceId((prev) => (prev === row.id ? null : prev)), 1500);
    } catch {
      toast({ title: "Copy failed", description: "Unable to access the clipboard.", variant: "destructive" });
    }
  }, [toast]);

  const [filterOpen, setFilterOpen] = useState(false);
  const [page, setPage] = useState(1);
  // AR-210 (TC-05.4): First/Prev/Next/Last become disabled at the ends, which
  // dropped focus to <body>. When that happens, move focus to the current page
  // button instead.
  const keepPagerFocus = () => {
    window.requestAnimationFrame(() => {
      const active = document.activeElement as HTMLButtonElement | null;
      if (active && active !== document.body && !active.disabled) return;
      document.getElementById("invoice-log-pager")?.querySelector<HTMLElement>('[aria-current="page"]')?.focus();
    });
  };
  const [sortBy, setSortBy] = useState<SortField>("createdAt");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");

  useEffect(() => {
    setPage(1);
  }, [viewMode, tramsAccountId]);
  const [filters, setFilters] = useState<InvoiceListFilters>({});
  const [textFilters, setTextFilters] = useState({
    advisor: "",
    primaryTraveler: "",
    supplier: "",
    confirmationNumber: "",
    invoiceNumber: "",
    recordLocator: "",
  });

  const appliedFilters = useMemo<InvoiceListFilters>(() => ({
    ...filters,
    advisor: textFilters.advisor || undefined,
    primaryTraveler: textFilters.primaryTraveler || undefined,
    supplier: textFilters.supplier || undefined,
    confirmationNumber: textFilters.confirmationNumber || undefined,
    invoiceNumber: textFilters.invoiceNumber || undefined,
    recordLocator: textFilters.recordLocator?.trim() || undefined,
  }), [filters, textFilters]);

  const { data, isLoading, isFetching, error, refetch } = useInvoiceList({
    page,
    pageSize: PAGE_SIZE,
    sortBy,
    sortDir,
    filters: appliedFilters,
    tramsAccountId,
    viewMode,
    refetchInterval: pendingVoidIds.length ? VOID_POLL_INTERVAL_MS : false,
  });

  const items = data?.items ?? [];
  const totalCount = data?.totalCount ?? 0;
  const totals = data?.totals;
  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));
  const effectiveViewMode = data?.viewMode ?? "my";
  const isCompanyWide = effectiveViewMode === "all";

  // AR-412: same A–Z → Z–A → default cycle as the Unclaimed table.
  const handleSort = useCallback((field: SortField) => {
    const next = nextSortState<SortField>(
      { sortBy, sortDir },
      field,
      { sortBy: "createdAt", sortDir: "desc" },
    );
    setSortBy(next.sortBy);
    setSortDir(next.sortDir);
    setPage(1);
  }, [sortBy, sortDir]);

  const [isExporting, setIsExporting] = useState(false);
  const handleExport = useCallback(async () => {
    setIsExporting(true);
    try {
      const qs = buildInvoiceListQuery({ sortBy, sortDir, filters: appliedFilters, tramsAccountId, viewMode });
      const res = await fetch(`/api/invoices/list/export?${qs.toString()}`, { credentials: "include" });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.message || "Failed to export invoices.");
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `invoice-log-${format(new Date(), "yyyy-MM-dd")}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast({
        title: "Export failed",
        description: err instanceof Error ? err.message : "Unable to export the invoice log.",
        variant: "destructive",
      });
    } finally {
      setIsExporting(false);
    }
  }, [sortBy, sortDir, appliedFilters, tramsAccountId, viewMode, toast]);

  const updateFilter = useCallback((key: keyof InvoiceListFilters, value: string | undefined) => {
    setFilters((prev) => ({ ...prev, [key]: value || undefined }));
    setPage(1);
  }, []);

  const updateTextFilter = useCallback((key: string, value: string) => {
    setTextFilters((prev) => ({ ...prev, [key]: value }));
    setPage(1);
  }, []);

  const clearAllFilters = useCallback(() => {
    setFilters({});
    setTextFilters({ advisor: "", primaryTraveler: "", supplier: "", confirmationNumber: "", invoiceNumber: "", recordLocator: "" });
    setPage(1);
  }, []);

  const hasActiveFilters = useMemo(() => {
    const nonDefaultFilters = Object.entries(filters).some(([, v]) => Boolean(v));
    return nonDefaultFilters || Object.values(textFilters).some((v) => v.trim() !== "");
  }, [filters, textFilters]);

  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (textFilters.primaryTraveler?.trim()) count++;
    if (textFilters.advisor?.trim()) count++;
    if (textFilters.supplier?.trim()) count++;
    if (textFilters.confirmationNumber?.trim()) count++;
    if (textFilters.invoiceNumber?.trim()) count++;
    if (textFilters.recordLocator?.trim()) count++;
    if (filters.invoiceStatusCode) count++;
    if (filters.departureDateFrom || filters.departureDateTo) count++;
    if (filters.returnDateFrom || filters.returnDateTo) count++;
    if (filters.createdFrom || filters.createdTo) count++;
    if (filters.paymentDateFrom || filters.paymentDateTo) count++;
    if (filters.totalFareMin || filters.totalFareMax) count++;
    if (filters.commissionMin || filters.commissionMax) count++;
    if (filters.missingClaim) count++;
    return count;
  }, [filters, textFilters]);

  const dateInversions = useMemo(() => ({
    departure: !!filters.departureDateFrom && !!filters.departureDateTo && filters.departureDateFrom > filters.departureDateTo,
    return: !!filters.returnDateFrom && !!filters.returnDateTo && filters.returnDateFrom > filters.returnDateTo,
    created: !!filters.createdFrom && !!filters.createdTo && filters.createdFrom > filters.createdTo,
    payment: !!filters.paymentDateFrom && !!filters.paymentDateTo && filters.paymentDateFrom > filters.paymentDateTo,
  }), [filters]);

  const closeVoidDialog = () => {
    setVoidTarget(null);
    setVoidReason("");
  };

  const handleVoidCancel = () => {
    if (voidReason.trim()) {
      setShowDiscardDialog(true);
    } else {
      closeVoidDialog();
    }
  };

  const handleVoidConfirm = async () => {
    if (!voidTarget) return;
    try {
      await voidInvoice.mutateAsync({
        id: voidTarget.id,
        reason: voidReason.trim() || undefined,
        operationId: crypto.randomUUID(),
      });
      setPendingVoidIds((prev) => (
        prev.some((entry) => entry.id === voidTarget.id)
          ? prev
          : [...prev, { id: voidTarget.id, startedAt: Date.now() }]
      ));
      toast({
        title: "Void request submitted",
      });
    } catch (err: any) {
      if (err instanceof InvoiceLockedByClaimError) {
        toast({
          title: "Cannot void this invoice",
          description: `Linked to unclaimed-commission claim #${err.claimId ?? "?"} under Worldvia support review. Contact support to resolve the claim first.`,
          variant: "destructive",
        });
      } else {
        toast({ title: "Void Failed", description: err.message || "Failed to void invoice.", variant: "destructive" });
      }
    }
    closeVoidDialog();
  };

  useEffect(() => {
    if (!pendingVoidIds.length) return;
    const now = Date.now();
    const itemsById = new Map(items.map((item) => [item.id, item]));
    setPendingVoidIds((prev) => {
      const next = prev.filter((entry) => {
        if (now - entry.startedAt > VOID_POLL_TIMEOUT_MS) return false;
        const match = itemsById.get(entry.id);
        return !(match && match.syncStatus === "connected");
      });
      return next.length === prev.length ? prev : next;
    });
  }, [items, pendingVoidIds.length]);

  useEffect(() => {
    if (!pendingVoidIds.length) return;
    const timer = window.setInterval(() => {
      const now = Date.now();
      setPendingVoidIds((prev) => {
        const next = prev.filter((entry) => now - entry.startedAt <= VOID_POLL_TIMEOUT_MS);
        return next.length === prev.length ? prev : next;
      });
    }, VOID_POLL_INTERVAL_MS);
    return () => window.clearInterval(timer);
  }, [pendingVoidIds.length]);

  if (error) {
    return (
      <SectionLoadError
        sectionName="Invoice Log"
        error={error}
        onRetry={() => refetch()}
      />
    );
  }

  return (
    <section aria-labelledby="invoice-log-heading">
      {/* AR-316 — v9 section header: label, the count folded in, then a rule. */}
      <div className="flex items-center gap-3 mb-3">
        <div className="flex items-center gap-2">
          <h2 id="invoice-log-heading" className="text-lg font-semibold tracking-tight">
            Invoice Log
          </h2>
          <span className="text-sm text-muted-foreground" role="status" aria-live="polite" aria-atomic="true">
            {totalCount.toLocaleString()} invoice{totalCount !== 1 ? "s" : ""}
          </span>
          {/* AR-210 (TC-07.2): the copy button's check icon was visual-only feedback. */}
          <span className="sr-only" role="status" aria-live="polite">
            {copiedInvoiceId != null ? "Invoice number copied" : ""}
          </span>
          {isFetching && !isLoading && <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />}
        </div>

        <span aria-hidden className="h-px flex-1 bg-border" />

        <div className="flex items-center gap-1">
          <button
            className="p-1.5 rounded-md max-md:min-h-11 max-md:min-w-11 max-md:inline-flex max-md:items-center max-md:justify-center hover:bg-muted transition-colors text-muted-foreground disabled:opacity-50 disabled:pointer-events-none"
            aria-label="Export invoice log as CSV"
            title="Export CSV"
            onClick={handleExport}
            disabled={isExporting || totalCount === 0}
          >
            {isExporting ? <Loader2 className="h-4.5 w-4.5 animate-spin" /> : <Download className="h-4.5 w-4.5" />}
          </button>
          <Popover open={filterOpen} onOpenChange={setFilterOpen}>
          <PopoverTrigger asChild>
            <button
              className={`relative p-1.5 rounded-md max-md:min-h-11 max-md:min-w-11 max-md:inline-flex max-md:items-center max-md:justify-center hover:bg-muted transition-colors ${
                activeFilterCount > 0 ? "text-orange-600" : "text-muted-foreground"
              }`}
              aria-label="Invoice filters"
            >
              <Filter className="h-4.5 w-4.5" />
              {activeFilterCount > 0 && (
                <span className="absolute -top-0.5 -right-0.5 flex h-3.5 w-3.5 items-center justify-center rounded-full bg-orange-500 text-[9px] font-bold text-white">
                  {activeFilterCount}
                </span>
              )}
            </button>
          </PopoverTrigger>
          {/* AR-316: top padding keeps the panel clear of the 35px GlobalHeader,
                which it previously opened straight over on mobile. */}
          <PopoverContent
            className="w-[min(20rem,calc(100vw-2rem))] max-h-[var(--radix-popover-content-available-height)] overflow-y-auto"
            align="end"
            collisionPadding={{ top: 43, right: 8, bottom: 8, left: 8 }}
            aria-labelledby="invoice-filters-title"
            onKeyDown={(e) => {
              // AR-210 (TC-16.3): Escape must close the panel even while focus is in a text box.
              // Leave it to an open dropdown inside the panel (portalled listbox, bubbled keydown).
              if (
                e.key === "Escape" &&
                !e.defaultPrevented &&
                !(e.target as HTMLElement).closest('[role="listbox"]')
              ) {
                e.preventDefault();
                setFilterOpen(false);
              }
            }}
          >
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <p id="invoice-filters-title" className="text-sm font-semibold">Filters</p>
                {activeFilterCount > 0 && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 text-xs"
                    onClick={() => {
                      clearAllFilters();
                      // AR-210 (TC-06.6): the button unmounts once no filter is active; keep focus in the panel.
                      window.requestAnimationFrame(() => filterTravelerRef.current?.focus());
                    }}
                  >
                    Reset all
                  </Button>
                )}
              </div>

              {/* Text search filters */}
              <div className="space-y-2">
                <label className="text-xs font-medium text-muted-foreground">Search</label>
                <Input placeholder="Traveler..." className="h-11 text-xs md:h-8" aria-label="Traveler" ref={filterTravelerRef} autoComplete="off" value={textFilters.primaryTraveler} onChange={(e) => updateTextFilter("primaryTraveler", e.target.value)} />
                <Input placeholder="Supplier..." className="h-11 text-xs md:h-8" aria-label="Supplier" autoComplete="off" value={textFilters.supplier} onChange={(e) => updateTextFilter("supplier", e.target.value)} />
                <div className="grid grid-cols-2 gap-2">
                  <Input placeholder="Invoice #..." className="h-11 text-xs md:h-8" aria-label="Invoice number" autoComplete="off" value={textFilters.invoiceNumber} onChange={(e) => updateTextFilter("invoiceNumber", e.target.value)} />
                  <Input placeholder="Confirm #..." className="h-11 text-xs md:h-8" aria-label="Confirmation number" autoComplete="off" value={textFilters.confirmationNumber} onChange={(e) => updateTextFilter("confirmationNumber", e.target.value)} />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <Input placeholder="Trip ID..." className="h-11 text-xs md:h-8" aria-label="Trip ID" autoComplete="off" value={textFilters.recordLocator} onChange={(e) => updateTextFilter("recordLocator", e.target.value)} />
                  <Input placeholder="Advisor..." className="h-11 text-xs md:h-8" aria-label="Advisor" autoComplete="off" value={textFilters.advisor} onChange={(e) => updateTextFilter("advisor", e.target.value)} />
                </div>
              </div>

              {/* Status filters */}
              <div className="space-y-2">
                <label className="text-xs font-medium text-muted-foreground">Status</label>
                <Select value={filters.invoiceStatusCode || "__all__"} onValueChange={(v) => updateFilter("invoiceStatusCode", v === "__all__" ? undefined : v)}>
                  <SelectTrigger aria-label="Invoice status" className="h-11 text-xs md:h-8"><SelectValue placeholder="Invoice Status" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__all__">All Status</SelectItem>
                    <SelectItem value="O">Open</SelectItem>
                    <SelectItem value="C">Closed</SelectItem>
                    <SelectItem value="V">Voided</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Needs review toggle */}
              <label className="flex items-center gap-2 cursor-pointer" data-testid="filter-missing-claim">
                <input
                  type="checkbox"
                  className="h-4 w-4 rounded border-gray-300 accent-orange-600"
                  checked={!!filters.missingClaim}
                  onChange={(e) => {
                    const turningOn = e.target.checked;
                    setFilters((prev) => ({
                      ...prev,
                      missingClaim: turningOn ? true : undefined,
                    }));
                    setPage(1);
                  }}
                />
                <span className="text-xs font-medium">Needs review (orphan invoices)</span>
              </label>

              {/* Date range filters */}
              <div className="space-y-2">
                <label className="text-xs font-medium text-muted-foreground flex items-center gap-1">
                  <CalendarRange className="h-3 w-3" />
                  Date Ranges
                </label>
                <div className="space-y-1.5">
                  <label className="text-xs text-muted-foreground">Depart</label>
                  <div className="grid grid-cols-2 gap-2">
                    <Input type="date" className={`h-11 text-xs md:h-8 ${dateInversions.departure ? "border-rose-400" : ""}`} aria-label="Depart date from" value={filters.departureDateFrom || ""} max={filters.departureDateTo || undefined} onChange={(e) => updateFilter("departureDateFrom", e.target.value)} />
                    <Input type="date" className={`h-11 text-xs md:h-8 ${dateInversions.departure ? "border-rose-400" : ""}`} aria-label="Depart date to" value={filters.departureDateTo || ""} min={filters.departureDateFrom || undefined} onChange={(e) => updateFilter("departureDateTo", e.target.value)} />
                  </div>
                  {dateInversions.departure && <p role="alert" className="text-xs font-medium text-rose-600 dark:text-rose-400">"From" must be on or before "To".</p>}
                </div>
                <div className="space-y-1.5">
                  <label className="text-xs text-muted-foreground">Return</label>
                  <div className="grid grid-cols-2 gap-2">
                    <Input type="date" className={`h-11 text-xs md:h-8 ${dateInversions.return ? "border-rose-400" : ""}`} aria-label="Return date from" value={filters.returnDateFrom || ""} max={filters.returnDateTo || undefined} onChange={(e) => updateFilter("returnDateFrom", e.target.value)} />
                    <Input type="date" className={`h-11 text-xs md:h-8 ${dateInversions.return ? "border-rose-400" : ""}`} aria-label="Return date to" value={filters.returnDateTo || ""} min={filters.returnDateFrom || undefined} onChange={(e) => updateFilter("returnDateTo", e.target.value)} />
                  </div>
                  {dateInversions.return && <p role="alert" className="text-xs font-medium text-rose-600 dark:text-rose-400">"From" must be on or before "To".</p>}
                </div>
                <div className="space-y-1.5">
                  <label className="text-xs text-muted-foreground">Created</label>
                  <div className="grid grid-cols-2 gap-2">
                    <Input type="date" className={`h-11 text-xs md:h-8 ${dateInversions.created ? "border-rose-400" : ""}`} aria-label="Created date from" value={filters.createdFrom || ""} max={filters.createdTo || undefined} onChange={(e) => updateFilter("createdFrom", e.target.value)} />
                    <Input type="date" className={`h-11 text-xs md:h-8 ${dateInversions.created ? "border-rose-400" : ""}`} aria-label="Created date to" value={filters.createdTo || ""} min={filters.createdFrom || undefined} onChange={(e) => updateFilter("createdTo", e.target.value)} />
                  </div>
                  {dateInversions.created && <p role="alert" className="text-xs font-medium text-rose-600 dark:text-rose-400">"From" must be on or before "To".</p>}
                </div>
                <div className="space-y-1.5">
                  <label className="text-xs text-muted-foreground">Statement Date</label>
                  <div className="grid grid-cols-2 gap-2">
                    <Input type="date" className={`h-11 text-xs md:h-8 ${dateInversions.payment ? "border-rose-400" : ""}`} aria-label="Statement date from" value={filters.paymentDateFrom || ""} max={filters.paymentDateTo || undefined} onChange={(e) => updateFilter("paymentDateFrom", e.target.value)} />
                    <Input type="date" className={`h-11 text-xs md:h-8 ${dateInversions.payment ? "border-rose-400" : ""}`} aria-label="Statement date to" value={filters.paymentDateTo || ""} min={filters.paymentDateFrom || undefined} onChange={(e) => updateFilter("paymentDateTo", e.target.value)} />
                  </div>
                  {dateInversions.payment && <p role="alert" className="text-xs font-medium text-rose-600 dark:text-rose-400">"From" must be on or before "To".</p>}
                </div>
              </div>

              {/* Amount range filters */}
              <div className="space-y-2">
                <label className="text-xs font-medium text-muted-foreground flex items-center gap-1">
                  <DollarSign className="h-3 w-3" />
                  Amount Ranges
                </label>
                <div className="space-y-1.5">
                  <label className="text-xs text-muted-foreground">Total Sale Amount</label>
                  <div className="grid grid-cols-2 gap-2">
                    <Input type="number" step="0.01" min="0" placeholder="Min" className="h-11 text-xs md:h-8" aria-label="Total sale amount minimum" value={filters.totalFareMin || ""} onChange={(e) => updateFilter("totalFareMin", e.target.value)} />
                    <Input type="number" step="0.01" min="0" placeholder="Max" className="h-11 text-xs md:h-8" aria-label="Total sale amount maximum" value={filters.totalFareMax || ""} onChange={(e) => updateFilter("totalFareMax", e.target.value)} />
                  </div>
                </div>
                <div className="space-y-1.5">
                  <label className="text-xs text-muted-foreground">Gross Commission</label>
                  <div className="grid grid-cols-2 gap-2">
                    <Input type="number" step="0.01" min="0" placeholder="Min" className="h-11 text-xs md:h-8" aria-label="Gross commission minimum" value={filters.commissionMin || ""} onChange={(e) => updateFilter("commissionMin", e.target.value)} />
                    <Input type="number" step="0.01" min="0" placeholder="Max" className="h-11 text-xs md:h-8" aria-label="Gross commission maximum" value={filters.commissionMax || ""} onChange={(e) => updateFilter("commissionMax", e.target.value)} />
                  </div>
                </div>
              </div>
            </div>
          </PopoverContent>
          </Popover>
        </div>
      </div>

      {/* Active filter pills */}
      {activeFilterCount > 0 && (
        <div className="flex flex-wrap items-center gap-1.5 mb-3">
          {textFilters.primaryTraveler?.trim() && (
            <span className="inline-flex items-center gap-1 rounded-full bg-orange-100 px-2 py-0.5 text-xs font-medium text-orange-700 dark:bg-orange-900/30 dark:text-orange-300">
              Traveler: {textFilters.primaryTraveler}
              <button aria-label="Clear Traveler filter" onClick={() => updateTextFilter("primaryTraveler", "")}><X className="h-2.5 w-2.5" /></button>
            </span>
          )}
          {textFilters.advisor?.trim() && (
            <span className="inline-flex items-center gap-1 rounded-full bg-purple-100 px-2 py-0.5 text-xs font-medium text-purple-700 dark:bg-purple-900/30 dark:text-purple-300">
              Advisor: {textFilters.advisor}
              <button aria-label="Clear Advisor filter" onClick={() => updateTextFilter("advisor", "")}><X className="h-2.5 w-2.5" /></button>
            </span>
          )}
          {textFilters.supplier?.trim() && (
            <span className="inline-flex items-center gap-1 rounded-full bg-orange-100 px-2 py-0.5 text-xs font-medium text-orange-700 dark:bg-orange-900/30 dark:text-orange-300">
              Supplier: {textFilters.supplier}
              <button aria-label="Clear Supplier filter" onClick={() => updateTextFilter("supplier", "")}><X className="h-2.5 w-2.5" /></button>
            </span>
          )}
          {textFilters.invoiceNumber?.trim() && (
            <span className="inline-flex items-center gap-1 rounded-full bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-700 dark:bg-blue-900/30 dark:text-blue-300">
              Invoice #: {textFilters.invoiceNumber}
              <button aria-label="Clear Invoice # filter" onClick={() => updateTextFilter("invoiceNumber", "")}><X className="h-2.5 w-2.5" /></button>
            </span>
          )}
          {textFilters.confirmationNumber?.trim() && (
            <span className="inline-flex items-center gap-1 rounded-full bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-700 dark:bg-blue-900/30 dark:text-blue-300">
              Confirm #: {textFilters.confirmationNumber}
              <button aria-label="Clear Confirmation # filter" onClick={() => updateTextFilter("confirmationNumber", "")}><X className="h-2.5 w-2.5" /></button>
            </span>
          )}
          {textFilters.recordLocator?.trim() && (
            <span className="inline-flex items-center gap-1 rounded-full bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-700 dark:bg-blue-900/30 dark:text-blue-300">
              Trip ID: {textFilters.recordLocator}
              <button aria-label="Clear Trip ID filter" onClick={() => updateTextFilter("recordLocator", "")}><X className="h-2.5 w-2.5" /></button>
            </span>
          )}
          {filters.invoiceStatusCode && (
            <span className="inline-flex items-center gap-1 rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-700 dark:bg-green-900/30 dark:text-green-300">
              Status: {filters.invoiceStatusCode === "O" ? "Open" : filters.invoiceStatusCode === "C" ? "Closed" : "Voided"}
              <button aria-label="Clear Status filter" onClick={() => updateFilter("invoiceStatusCode", undefined)}><X className="h-2.5 w-2.5" /></button>
            </span>
          )}
          {filters.missingClaim && (
            <span className="inline-flex items-center gap-1 rounded-full bg-red-100 px-2 py-0.5 text-xs font-medium text-red-700 dark:bg-red-900/30 dark:text-red-300">
              Needs review
              <button aria-label="Clear Needs review filter" onClick={() => { setFilters((prev) => ({ ...prev, missingClaim: undefined })); setPage(1); }}><X className="h-2.5 w-2.5" /></button>
            </span>
          )}
          {(filters.departureDateFrom || filters.departureDateTo) && (
            <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-700 dark:bg-amber-900/30 dark:text-amber-300">
              Depart: {filters.departureDateFrom || "…"} – {filters.departureDateTo || "…"}
              <button aria-label="Clear Departure dates filter" onClick={() => { updateFilter("departureDateFrom", undefined); updateFilter("departureDateTo", undefined); }}><X className="h-2.5 w-2.5" /></button>
            </span>
          )}
          {(filters.returnDateFrom || filters.returnDateTo) && (
            <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-700 dark:bg-amber-900/30 dark:text-amber-300">
              Return: {filters.returnDateFrom || "…"} – {filters.returnDateTo || "…"}
              <button aria-label="Clear Return dates filter" onClick={() => { updateFilter("returnDateFrom", undefined); updateFilter("returnDateTo", undefined); }}><X className="h-2.5 w-2.5" /></button>
            </span>
          )}
          {(filters.createdFrom || filters.createdTo) && (
            <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-700 dark:bg-amber-900/30 dark:text-amber-300">
              Created: {filters.createdFrom || "…"} – {filters.createdTo || "…"}
              <button aria-label="Clear Created dates filter" onClick={() => { updateFilter("createdFrom", undefined); updateFilter("createdTo", undefined); }}><X className="h-2.5 w-2.5" /></button>
            </span>
          )}
          {(filters.paymentDateFrom || filters.paymentDateTo) && (
            <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-700 dark:bg-amber-900/30 dark:text-amber-300">
              Statement: {filters.paymentDateFrom || "…"} – {filters.paymentDateTo || "…"}
              <button aria-label="Clear Statement Date filter" onClick={() => { updateFilter("paymentDateFrom", undefined); updateFilter("paymentDateTo", undefined); }}><X className="h-2.5 w-2.5" /></button>
            </span>
          )}
          {(filters.totalFareMin || filters.totalFareMax) && (
            <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-medium text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300">
              Total: ${filters.totalFareMin || "0"} – ${filters.totalFareMax || "∞"}
              <button aria-label="Clear Total Sale Amount filter" onClick={() => { updateFilter("totalFareMin", undefined); updateFilter("totalFareMax", undefined); }}><X className="h-2.5 w-2.5" /></button>
            </span>
          )}
          {(filters.commissionMin || filters.commissionMax) && (
            <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-medium text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300">
              Gross Comm.: ${filters.commissionMin || "0"} – ${filters.commissionMax || "∞"}
              <button aria-label="Clear Gross Commission filter" onClick={() => { updateFilter("commissionMin", undefined); updateFilter("commissionMax", undefined); }}><X className="h-2.5 w-2.5" /></button>
            </span>
          )}
        </div>
      )}

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="flex items-center justify-center p-16">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : items.length === 0 ? (
            <div className="flex flex-col items-center justify-center p-16 text-center">
              <FileText className="h-12 w-12 text-muted-foreground/40 mb-4" />
              <h3 className="text-lg font-medium">
                {hasActiveFilters ? "No invoices match your filters" : "No invoices yet"}
              </h3>
              <p className="text-sm text-muted-foreground mt-1 mb-4">
                {hasActiveFilters ? "Try adjusting your filters or clearing them." : "Create your first invoice to get started."}
              </p>
              {hasActiveFilters ? (
                <Button variant="outline" onClick={clearAllFilters}>Clear Filters</Button>
              ) : (
                <Button onClick={() => setLocation(FINANCES_PATHS.invoicesNew)}>
                  <Plus className="h-4 w-4 mr-2" /> Create Invoice
                </Button>
              )}
            </div>
          ) : (
            <div className={`overflow-x-auto ${financesTable.shell}`}>
              <Table>
                <TableHeader>
                  <TableRow className={financesTable.headerRow}>
                    {isCompanyWide && <SortableHeader label="Advisor" field="advisorName" currentSort={sortBy} currentDir={sortDir} onSort={handleSort} className="hidden md:table-cell" />}
                    <SortableHeader label="Trip ID" field="recordLocator" currentSort={sortBy} currentDir={sortDir} onSort={handleSort} className="hidden md:table-cell" />
                    <SortableHeader label="Invoice #" field="invoiceNumber" currentSort={sortBy} currentDir={sortDir} onSort={handleSort} className="hidden md:table-cell" />
                    <SortableHeader label="Primary Traveler" field="primaryTraveler" currentSort={sortBy} currentDir={sortDir} onSort={handleSort} />
                    <SortableHeader label="Supplier" field="supplierName" currentSort={sortBy} currentDir={sortDir} onSort={handleSort} className="hidden md:table-cell" />
                    <SortableHeader label="Confirm #" field="confirmationNumber" currentSort={sortBy} currentDir={sortDir} onSort={handleSort} className="hidden md:table-cell" />
                    <SortableHeader label="Depart" field="departureDate" currentSort={sortBy} currentDir={sortDir} onSort={handleSort} className="hidden md:table-cell" />
                    <SortableHeader label="Return" field="returnDate" currentSort={sortBy} currentDir={sortDir} onSort={handleSort} className="hidden md:table-cell" />
                    <SortableHeader label="Total" field="totalFare" currentSort={sortBy} currentDir={sortDir} onSort={handleSort} className="hidden text-right md:table-cell" />
                    <SortableHeader label="Gross Comm." field="commissionAmount" currentSort={sortBy} currentDir={sortDir} onSort={handleSort} className="hidden text-right md:table-cell" />
                    {/* Net Comm. (Agency Owners only) — payable owner/lead split */}
                    {showNetCommission && (
                      <TableHead className="hidden text-right md:table-cell">
                        <span className={`${financesTable.headStatic} whitespace-nowrap`}>Net Comm.</span>
                      </TableHead>
                    )}
                    <SortableHeader label="Status" field="invoiceStatusCode" currentSort={sortBy} currentDir={sortDir} onSort={handleSort} className="hidden md:table-cell" />
                    <SortableHeader label="Created" field="createdAt" currentSort={sortBy} currentDir={sortDir} onSort={handleSort} className="hidden md:table-cell" />
                    <SortableHeader label="Statement Date" field="paymentDate" currentSort={sortBy} currentDir={sortDir} onSort={handleSort} className="hidden md:table-cell" />
                    <TableHead className="w-[80px] md:w-[80px]" scope="col">
                      <span className="sr-only">Row actions</span>
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {items.map((row) => {
                    const status = invoiceStatusConfig[row.invoiceStatusCode ?? "O"];
                    const isOpen = row.invoiceStatusCode === "O";
                    const isCreator = effectiveUserId === row.createdBy;
                    const isPendingVoid = row.status === "voided" && isOpen;
                    // An active unclaimed-commission claim locks both
                    // edit and void until support resolves it (Mr.
                    // Pickles, 2026-05-12). Server enforces the same
                    // rule with 409 INVOICE_LOCKED_BY_UNCLAIMED_CLAIM.
                    const lockedByClaim = row.lockedByClaim;
                    const editAllowed = isOpen && isCreator && !isPendingVoid && !lockedByClaim;
                    const voidAllowed = isOpen && isCreator && !isPendingVoid && !lockedByClaim;
                    const payment = formatPaymentDate(row);
                    const isOwnRow = row.userId === effectiveUserId;

                    const editTooltip = lockedByClaim
                      ? `Locked — under Worldvia support review (claim #${lockedByClaim.claimId}).`
                      : isPendingVoid
                      ? "This invoice cannot be edited while a void request is in progress"
                      : !isOpen ? "Only Open invoices can be edited"
                      : !isCreator ? "Only the invoice creator can edit"
                      : "Edit invoice";
                    const voidTooltip = lockedByClaim
                      ? `Locked — under Worldvia support review (claim #${lockedByClaim.claimId}).`
                      : isPendingVoid
                      ? "A void request is already in progress for this invoice"
                      : !isOpen ? "Only Open invoices can be voided"
                      : !isCreator ? "Only the invoice creator can void"
                      : "Void invoice";

                    const canOpenFromLog = isCreator;

                    const navigateToTripEdit = () => {
                      if (!row.tripId) {
                        toast({
                          title: "No trip linked",
                          description: "This invoice is not assigned to a trip.",
                          variant: "destructive",
                        });
                        return;
                      }
                      if (!canOpenFromLog) {
                        toast({
                          title: "Cannot open invoice",
                          description: "Only the invoice creator can open this invoice from the log.",
                          variant: "destructive",
                        });
                        return;
                      }
                      setLocation(
                        FINANCES_PATHS.tripEdit(
                          row.tripId,
                          row.id,
                          row.tramsAccountId ?? undefined,
                        ),
                        {
                          state: {
                            highlightInvoiceId: row.id,
                            tramsAccountId: row.tramsAccountId ?? undefined,
                          },
                        },
                      );
                    };

                    const rowAriaLabel = canOpenFromLog && row.tripId && row.id
                      ? `Open invoice ${formatTramsInvoiceDisplayNumber(row.tramsInvoiceNumber)} for trip ${row.tripId}`
                      : row.tripId
                        ? `Invoice ${formatTramsInvoiceDisplayNumber(row.tramsInvoiceNumber)} for trip ${row.tripId}`
                        : "Invoice details";

                    return (
                      <TableRow
                        key={row.id}
                        role={canOpenFromLog ? "button" : undefined}
                        tabIndex={canOpenFromLog ? 0 : -1}
                        aria-label={rowAriaLabel}
                        className={`${canOpenFromLog ? "cursor-pointer hover:bg-muted/40" : "cursor-default"} focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 ${isPendingVoid ? "opacity-60" : ""} ${isCompanyWide && isOwnRow ? "bg-primary/[0.03]" : ""}`}
                        onClick={canOpenFromLog ? navigateToTripEdit : undefined}
                        onKeyDown={canOpenFromLog ? (event) => {
                          if (event.target !== event.currentTarget) return;
                          if (event.key === "Enter" || event.key === " " || event.key === "Spacebar") {
                            event.preventDefault();
                            navigateToTripEdit();
                          }
                        } : undefined}
                      >
                        {isCompanyWide && (
                          <TableCell className="hidden md:table-cell">
                            <div className="flex items-center gap-1.5">
                              {isOwnRow && <span className="inline-block h-1.5 w-1.5 rounded-full bg-primary shrink-0" />}
                              <span className={isOwnRow ? "font-medium" : ""}>{row.advisorName ?? "—"}</span>
                            </div>
                          </TableCell>
                        )}
                        <TableCell className="hidden md:table-cell">{row.recordLocator ?? "—"}</TableCell>
                        <TableCell className="hidden md:table-cell">
                          <div className="flex items-center gap-1.5">
                            <span>{formatTramsInvoiceDisplayNumber(row.tramsInvoiceNumber)}</span>
                            {row.tramsInvoiceNumber != null && row.tramsInvoiceNumber !== "" && (
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-5 w-5 text-muted-foreground hover:text-foreground"
                                aria-label={`Copy invoice number ${formatTramsInvoiceDisplayNumber(row.tramsInvoiceNumber)}`}
                                onClick={(event) => {
                                  event.stopPropagation();
                                  copyInvoiceNumber(row);
                                }}
                              >
                                {copiedInvoiceId === row.id ? <Check className="h-3 w-3 text-emerald-600" /> : <Copy className="h-3 w-3" />}
                              </Button>
                            )}
                            {lockedByClaim && (
                              <TooltipProvider>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <span className="inline-flex">
                                      <Lock className="h-3 w-3 text-amber-600" />
                                    </span>
                                  </TooltipTrigger>
                                  <TooltipContent>
                                    Linked to unclaimed claim #{lockedByClaim.claimId} — read-only while under Worldvia support review.
                                  </TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                            )}
                          </div>
                        </TableCell>
                        {/* AR-316: below `md` this is the only content cell, so it
                            carries the rest of the row as a stacked summary — a
                            13-column table needed 1346px of sideways scrolling
                            on a phone. Every value below is also rendered in its
                            own `md:table-cell` column for desktop. */}
                        <TableCell className="max-w-none truncate font-medium md:max-w-[160px]">
                          <span className="block truncate">{row.primaryTraveler ?? "—"}</span>
                          <span className="mt-1 flex flex-col gap-0.5 text-xs font-normal text-muted-foreground md:hidden">
                            <span className="flex flex-wrap items-center gap-x-1.5">
                              <span>{formatTramsInvoiceDisplayNumber(row.tramsInvoiceNumber)}</span>
                              {/* Same copy control as the desktop Invoice # column —
                                  below `md` that column is hidden, so without this a
                                  phone had no way to copy the invoice number. */}
                              {row.tramsInvoiceNumber != null && row.tramsInvoiceNumber !== "" && (
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-5 w-5 text-muted-foreground hover:text-foreground"
                                  aria-label={`Copy invoice number ${formatTramsInvoiceDisplayNumber(row.tramsInvoiceNumber)}`}
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    copyInvoiceNumber(row);
                                  }}
                                >
                                  {copiedInvoiceId === row.id ? <Check className="h-3 w-3 text-emerald-600" /> : <Copy className="h-3 w-3" />}
                                </Button>
                              )}
                              {row.recordLocator && <span aria-hidden>·</span>}
                              {row.recordLocator && <span>{row.recordLocator}</span>}
                              <span className={`${STATUS_CHIP} ${status?.chip ?? ""}`}>
                                {status?.label ?? row.invoiceStatusCode}
                              </span>
                            </span>
                            <span className="truncate">{row.supplierName ?? "—"}</span>
                            {/* AR-316 review: in company-wide mode the Advisor
                                column is the desktop-only one, so on a phone the
                                row had nothing saying whose invoice it is. */}
                            {isCompanyWide && (
                              <span className="flex items-center gap-1.5 truncate">
                                {isOwnRow && <span aria-hidden className="inline-block h-1.5 w-1.5 shrink-0 rounded-full bg-primary" />}
                                <span className={`truncate ${isOwnRow ? "font-medium text-foreground" : ""}`}>
                                  {row.advisorName ?? "—"}
                                </span>
                              </span>
                            )}
                            {(row.departureDate || row.returnDate) && (
                              <span className="whitespace-nowrap">
                                {row.departureDate ? format(parseDateAsLocal(row.departureDate), "MM/dd/yy") : "—"}
                                {" – "}
                                {row.returnDate ? format(parseDateAsLocal(row.returnDate), "MM/dd/yy") : "—"}
                              </span>
                            )}
                            <span className="whitespace-nowrap">
                              Gross Comm. ${parseFloat(row.commissionAmount || "0").toLocaleString("en-US", { minimumFractionDigits: 2 })}
                              {" · "}
                              {payment.text}
                              {payment.isInterim && <Badge variant="outline" className="ml-1.5 px-1 py-0 text-[10px]">Future</Badge>}
                            </span>
                          </span>
                        </TableCell>
                        <TableCell className="hidden max-w-[140px] truncate md:table-cell">{row.supplierName ?? "—"}</TableCell>
                        <TableCell className="hidden md:table-cell">{row.confirmationNumber ?? "—"}</TableCell>
                        <TableCell className="hidden whitespace-nowrap md:table-cell">{row.departureDate ? format(parseDateAsLocal(row.departureDate), "MM/dd/yyyy") : "—"}</TableCell>
                        <TableCell className="hidden whitespace-nowrap md:table-cell">{row.returnDate ? format(parseDateAsLocal(row.returnDate), "MM/dd/yyyy") : "—"}</TableCell>
                        <TableCell className="hidden text-right font-medium whitespace-nowrap md:table-cell">
                          ${parseFloat(row.totalFare || "0").toLocaleString("en-US", { minimumFractionDigits: 2 })}
                        </TableCell>
                        <TableCell className="hidden text-right whitespace-nowrap md:table-cell">
                          ${parseFloat(row.commissionAmount || "0").toLocaleString("en-US", { minimumFractionDigits: 2 })}
                        </TableCell>
                        {showNetCommission && (
                          <TableCell className="hidden text-right whitespace-nowrap md:table-cell">
                            {row.agentCommissionAmount != null &&
                            row.agentCommissionAmount !== ""
                              ? `$${parseFloat(row.agentCommissionAmount || "0").toLocaleString("en-US", { minimumFractionDigits: 2 })}`
                              : "—"}
                          </TableCell>
                        )}
                        <TableCell className="hidden md:table-cell">
                          <span className={`${STATUS_CHIP} ${status?.chip ?? ""}`}>
                            {status?.label ?? row.invoiceStatusCode}
                          </span>
                        </TableCell>
                        <TableCell className="hidden whitespace-nowrap md:table-cell">{format(new Date(row.createdAt), "MM/dd/yyyy")}</TableCell>
                        <TableCell className="hidden whitespace-nowrap md:table-cell">
                          {payment.text}
                          {payment.isInterim && <Badge variant="outline" className="ml-1.5 text-xs px-1 py-0">Future</Badge>}
                        </TableCell>
                        <TableCell onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
                          <div className="flex flex-col items-center gap-0.5 md:flex-row">
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  {/* AR-210 (TC-07.3, TC-26.3): the wrapper is a focus stop only while
                                      the button is disabled (so its tooltip reason stays reachable), and
                                      then it carries the disabled button's name and state. */}
                                  <span
                                    tabIndex={editAllowed ? undefined : 0}
                                    role={editAllowed ? undefined : "button"}
                                    aria-disabled={editAllowed ? undefined : true}
                                    aria-label={editAllowed ? undefined : `Edit invoice ${formatTramsInvoiceDisplayNumber(row.tramsInvoiceNumber)}`}
                                    className="inline-flex"
                                  >
                                    <Button variant="ghost" size="icon" aria-hidden={editAllowed ? undefined : true} aria-label={`Edit invoice ${formatTramsInvoiceDisplayNumber(row.tramsInvoiceNumber)}`} className="h-11 w-11 md:h-7 md:w-7" disabled={!editAllowed} onClick={(event) => {
                                      event.stopPropagation();
                                      if (!editAllowed) return;
                                      setLocation(FINANCES_PATHS.invoiceEdit(row.id));
                                    }}>
                                      <Pencil className="h-3.5 w-3.5" />
                                    </Button>
                                  </span>
                                </TooltipTrigger>
                                <TooltipContent>{editTooltip}</TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <span
                                    tabIndex={voidAllowed ? undefined : 0}
                                    role={voidAllowed ? undefined : "button"}
                                    aria-disabled={voidAllowed ? undefined : true}
                                    aria-label={voidAllowed ? undefined : `Void invoice ${formatTramsInvoiceDisplayNumber(row.tramsInvoiceNumber)}`}
                                    className="inline-flex"
                                  >
                                    <Button variant="ghost" size="icon" aria-hidden={voidAllowed ? undefined : true} aria-label={`Void invoice ${formatTramsInvoiceDisplayNumber(row.tramsInvoiceNumber)}`} className="h-11 w-11 text-destructive hover:text-destructive md:h-7 md:w-7" disabled={!voidAllowed} onClick={() => setVoidTarget(row)}>
                                      <Ban className="h-3.5 w-3.5" />
                                    </Button>
                                  </span>
                                </TooltipTrigger>
                                <TooltipContent>{voidTooltip}</TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <span className="inline-flex">
                                    <Button variant="ghost" size="icon" className="h-11 w-11 text-amber-600 hover:text-amber-700 md:h-7 md:w-7" aria-label={`Open commission inquiry for invoice ${formatTramsInvoiceDisplayNumber(row.tramsInvoiceNumber)}`} onClick={() => setInquiryInvoiceId(row.id)}>
                                      <HelpCircle className="h-3.5 w-3.5" />
                                    </Button>
                                  </span>
                                </TooltipTrigger>
                                <TooltipContent>Commission Inquiry</TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
                {/* AR-411: server-side sums over EVERY filtered row (not just
                    this page), aligned under the money columns. Hidden below
                    `md` — the totals bar under the card takes over there. */}
                <TableFooter className="hidden md:table-footer-group">
                  <TableRow data-testid="invoice-log-totals-row" className="hover:bg-transparent">
                    <TableCell colSpan={isCompanyWide ? 8 : 7} className="whitespace-nowrap">
                      <span className={financesTable.headStatic}>Totals</span>
                      <span className="ml-2 text-xs font-normal text-muted-foreground">
                        {totalCount.toLocaleString()} invoice{totalCount !== 1 ? "s" : ""}
                      </span>
                    </TableCell>
                    <TableCell className="text-right font-semibold tabular-nums whitespace-nowrap" data-testid="invoice-log-total-fare">
                      {formatTotal(totals?.totalFare)}
                    </TableCell>
                    <TableCell className="text-right font-semibold tabular-nums whitespace-nowrap" data-testid="invoice-log-total-commission">
                      {formatTotal(totals?.commissionAmount)}
                    </TableCell>
                    {showNetCommission && (
                      <TableCell className="text-right font-semibold tabular-nums whitespace-nowrap" data-testid="invoice-log-total-net">
                        {formatTotal(totals?.agentCommissionAmount)}
                      </TableCell>
                    )}
                    <TableCell colSpan={4} />
                  </TableRow>
                </TableFooter>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* AR-411: mobile totals (money columns are hidden below `md`), and the
          only totals surface when no rows match. */}
      {!isLoading && (
        <InvoiceLogTotalsBar
          totals={totals}
          totalCount={totalCount}
          showNetCommission={showNetCommission}
          className={items.length === 0 ? "" : "md:hidden"}
        />
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex flex-col items-start gap-2 mt-3 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-sm text-muted-foreground" role="status" aria-live="polite" aria-atomic="true">
            Showing {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, totalCount)} of {totalCount.toLocaleString()}
          </p>
          {/* AR-316: wraps instead of pushing the page sideways on mobile */}
          <nav id="invoice-log-pager" aria-label="Invoice log pages" className="flex flex-wrap items-center gap-1">
            <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => { setPage(1); keepPagerFocus(); }} className="h-11 text-xs md:h-8">First</Button>
            <Button variant="outline" size="icon" aria-label="Previous page" className="h-11 w-11 md:h-8 md:w-8" disabled={page <= 1} onClick={() => { setPage((p) => Math.max(1, p - 1)); keepPagerFocus(); }}>
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <div className="flex items-center gap-1 px-2">
              {generatePageNumbers(page, totalPages).map((p, i) =>
                p === "..." ? (
                  <span key={`e${i}`} className="px-1 text-xs text-muted-foreground">...</span>
                ) : (
                  <Button key={p} variant={p === page ? "default" : "outline"} size="sm" className="h-11 w-11 p-0 text-xs md:h-8 md:w-8" aria-label={`Page ${p}`} aria-current={p === page ? "page" : undefined} onClick={() => setPage(p as number)}>
                    {p}
                  </Button>
                ),
              )}
            </div>
            <Button variant="outline" size="icon" aria-label="Next page" className="h-11 w-11 md:h-8 md:w-8" disabled={page >= totalPages} onClick={() => { setPage((p) => Math.min(totalPages, p + 1)); keepPagerFocus(); }}>
              <ChevronRight className="h-4 w-4" />
            </Button>
            <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => { setPage(totalPages); keepPagerFocus(); }} className="h-11 text-xs md:h-8">Last</Button>
          </nav>
        </div>
      )}

      {/* Void Confirmation Dialog */}
      <AlertDialog open={!!voidTarget} onOpenChange={(open) => { if (!open) handleVoidCancel(); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Void Invoice
              {/* AR-210 (TC-11.3): the dialog never said which invoice it voids. */}
              {voidTarget?.tramsInvoiceNumber ? (
                <span className="sr-only"> {formatTramsInvoiceDisplayNumber(voidTarget.tramsInvoiceNumber)}</span>
              ) : null}
            </AlertDialogTitle>
            <VoidInvoiceConfirmDescription
              supplierName={voidTarget?.supplierName}
              totalFare={voidTarget?.totalFare}
            />
          </AlertDialogHeader>
          <div className="py-2">
            <Label htmlFor="void-reason" className="text-sm font-medium">
              Reason <span className="text-muted-foreground font-normal">(optional)</span>
            </Label>
            <Textarea
              id="void-reason"
              placeholder="e.g. Plans changed, duplicate booking..."
              value={voidReason}
              onChange={(e) => setVoidReason(e.target.value)}
              className="mt-1.5 text-sm"
              rows={2}
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={(e) => { e.preventDefault(); handleVoidCancel(); }}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); handleVoidConfirm(); }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={voidInvoice.isPending}
            >
              {voidInvoice.isPending ? "Voiding..." : "Void Invoice"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Discard void reason confirmation */}
      <UnsavedChangesDialog
        open={showDiscardDialog}
        onOpenChange={setShowDiscardDialog}
        onConfirm={() => {
          setShowDiscardDialog(false);
          closeVoidDialog();
        }}
        title="Discard Void Reason?"
        description="You have entered a void reason. If you cancel now, this information will be lost. Are you sure?"
        confirmLabel="Discard"
        cancelLabel="Go Back"
      />

      {/* Commission Inquiry Dialog */}
      <CommissionInquiryDialog
        open={inquiryInvoiceId != null}
        onOpenChange={(open) => { if (!open) setInquiryInvoiceId(null); }}
        invoiceId={inquiryInvoiceId}
      />
    </section>
  );
}
